import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Flex, Loader, Popover, Text, TextInput } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ExtendedColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue';

interface Props {
	column: ExtendedColumn<unknown>;
	filterText?: string;
	multiple?: boolean;
}

interface FilterOption {
	value: string;
	label: string;
}

const MAX_INITIAL_OPTIONS = 50;
const MIN_OPTIONS_FOR_SEARCH = 10;
const SEARCH_DEBOUNCE_MS = 300;
// Distance (px) from the bottom of the list at which we ask for the next page.
const LOAD_MORE_THRESHOLD_PX = 48;

const checkboxStyles = {
	body: {
		alignItems: 'center',
		padding: '0.5rem',
	},
	input: {
		border: `1px solid ${defaultTheme.v3.colors.dark[4]} !important`,
		borderRadius: '0.125rem !important',
		':checked': {
			backgroundColor: defaultTheme.v3.colors.blue[6],
			border: 'none !important',
		},
	},
	label: {
		fontSize: '0.875rem',
		fontWeight: 400,
	},
};

const inputStyles = {
	input: {
		borderColor: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
		cursor: 'pointer',
		'&:hover': {
			borderColor: defaultTheme.v3.colors.dark[0],
		},
		'&:focus': {
			borderColor: defaultTheme.v3.colors.dark[0],
		},
		'&:focus-within': {
			borderColor: defaultTheme.v3.colors.dark[0],
		},
	},
};

export const TableFilterCheckboxSelect = ({ column, filterText, multiple = false }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const { getFilterValue, setFilterValue, columnDef } = column;
	const [popoverOpened, setPopoverOpened] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const selectedValues = (() => {
		const filterValue = getFilterValue();

		if (multiple) {
			return (filterValue as string[]) ?? [];
		}

		return filterValue ? [filterValue as string] : [];
	})();

	const hasSelection = selectedValues.length > 0;

	// Server-driven mode is opt-in and only applies to the multi-select variant: the
	// dropdown lazy-loads on open, searches + paginates via these callbacks, and
	// renders `data` as-is (the server already filtered/limited it). Without
	// `serverSide` the component keeps its original client-side slice/filter.
	const multiSelectProps = columnDef.mantineFilterMultiSelectProps;
	const serverSide = multiple && !!multiSelectProps?.serverSide;
	const { onDropdownOpen, onSearchChange, onReachEnd, isLoading, hasMore } =
		multiSelectProps ?? {};

	// Lazy-enable the data source the first (and any) time the dropdown opens.
	useEffect(() => {
		if (popoverOpened && serverSide) {
			onDropdownOpen?.();
		}
	}, [popoverOpened, serverSide, onDropdownOpen]);

	// Push the debounced search term to the server-side data source.
	const debouncedSearchQuery = useDebounceValue(searchQuery, SEARCH_DEBOUNCE_MS);
	useEffect(() => {
		if (serverSide) {
			onSearchChange?.(debouncedSearchQuery);
		}
	}, [serverSide, debouncedSearchQuery, onSearchChange]);

	const allOptionsCount = multiple
		? (multiSelectProps?.data ?? []).length
		: (columnDef.mantineFilterSelectProps?.data ?? []).length;
	// Server-side lists are paginated/searchable, so always offer the search box.
	const shouldShowSearch = serverSide || allOptionsCount >= MIN_OPTIONS_FOR_SEARCH;

	const filteredOptions = useMemo(() => {
		const allOptions: FilterOption[] = multiple
			? multiSelectProps?.data ?? []
			: columnDef.mantineFilterSelectProps?.data ?? [];

		// Server-side already filtered/limited the result — render it directly.
		if (serverSide) {
			return allOptions;
		}

		if (!searchQuery) {
			return allOptions.slice(0, MAX_INITIAL_OPTIONS);
		}

		const query = searchQuery.toLowerCase();
		return allOptions.filter((option) => option.label.toLowerCase().includes(query));
	}, [
		multiSelectProps?.data,
		columnDef.mantineFilterSelectProps?.data,
		multiple,
		serverSide,
		searchQuery,
	]);

	const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
		if (!serverSide || !hasMore || isLoading) {
			return;
		}
		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
		if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) {
			onReachEnd?.();
		}
	};

	const clearFilter = (event: React.MouseEvent) => {
		event.stopPropagation();
		setFilterValue(multiple ? null : undefined);
		setSearchQuery('');
	};

	const toggleOption = (value: string) => {
		if (multiple) {
			const nextValues = selectedValues.includes(value)
				? selectedValues.filter((selectedValue) => selectedValue !== value)
				: [...selectedValues, value];

			setFilterValue(nextValues.length > 0 ? nextValues : null);
			return;
		}

		setFilterValue(selectedValues[0] === value ? undefined : value);
	};

	return (
		<Popover
			opened={popoverOpened}
			onChange={setPopoverOpened}
			position="bottom-start"
			withinPortal
			shadow="md"
			width="target"
			styles={{
				dropdown: {
					zIndex: Z_INDEX.MENU,
				},
			}}
		>
			<Popover.Target>
				<TextInput
					placeholder={filterText}
					value=""
					readOnly
					onClick={() => {
						if (!popoverOpened) {
							setPopoverOpened(true);
						}
					}}
					size="sm"
					rightSection={
						hasSelection ? (
							<Icon
								variant="close-v3"
								size="md"
								onClick={clearFilter}
								style={{ cursor: 'pointer' }}
							/>
						) : (
							<Icon
								variant="caret-down"
								size="lg"
								onClick={(event) => {
									event.stopPropagation();
									if (popoverOpened) {
										setPopoverOpened(!popoverOpened);
									}
								}}
							/>
						)
					}
					rightSectionProps={
						popoverOpened || hasSelection ? {} : { style: { pointerEvents: 'none' } }
					}
					styles={inputStyles}
				/>
			</Popover.Target>
			<Popover.Dropdown p="0.25rem">
				<Flex gap="0.25rem" direction="column">
					{shouldShowSearch && (
						<TextInput
							placeholder={tCommonUi('mantine_table.search_input_placeholder')}
							value={searchQuery}
							onChange={(event) => setSearchQuery(event.currentTarget.value)}
							size="sm"
							rightSection={<Icon variant="search-v3" size="md" />}
							styles={inputStyles}
						/>
					)}

					{filteredOptions.length === 0 && serverSide && isLoading && (
						<Flex justify="center" p="0.75rem">
							<Loader size="sm" />
						</Flex>
					)}

					{filteredOptions.length === 0 && !(serverSide && isLoading) && (
						<Text size="0.875rem" c={defaultTheme.v3.colors.dark[0]} p="0.5rem">
							{tCommonUi('select.no_options_found')}
						</Text>
					)}

					{filteredOptions.length > 0 && (
						<div
							style={{ maxHeight: '12rem', overflow: 'auto' }}
							onScroll={handleListScroll}
						>
							{filteredOptions.map((option) => (
								<Checkbox
									key={option.value}
									label={option.label}
									checked={selectedValues.includes(option.value)}
									onChange={(event) => {
										event.stopPropagation();
										toggleOption(option.value);
									}}
									size="0.875rem"
									styles={checkboxStyles}
								/>
							))}
							{serverSide && isLoading && (
								<Flex justify="center" p="0.5rem">
									<Loader size="sm" />
								</Flex>
							)}
						</div>
					)}
				</Flex>
			</Popover.Dropdown>
		</Popover>
	);
};
